/**
 * Session-independent assembly of a skinned canonical rig (RIG mode + read-only companions).
 *
 * The editor's unrigged pipeline (`src/editor/controllers/rigController.ts`) used to build this
 * inside its own closure. It is pure construction — imported meshes + a joint placement + stored
 * weights in, a bound rig out — so it lives here, where a viewer that loads a SECOND project
 * beside the open one can call it without touching the editor session.
 *
 * The layout is the one `createSkinnedMesh` requires and the one that survives a GLB round trip
 * (three #13897): every `SkinnedMesh` AND the skeleton's Root bone sit under a single identity
 * `Group`, with identity bind matrices throughout.
 *
 * No disposal happens here. The caller owns the returned objects; the editor deliberately keeps
 * the previous ones alive across a re-skin.
 */

import { Group } from 'three';
import type { Mesh, Skeleton, SkinnedMesh } from 'three';

import { AppError } from '../shared';
import type { CanonicalRigPlacement, SkinData } from '../shared';
import {
  bindingForPlacement,
  buildCanonicalSkeleton,
  builtRestJointPositions,
  createSkinnedMesh,
} from './skeletonBuilder';
import type { BuiltCanonicalSkeleton } from './skeletonBuilder';
import { bindRig } from '../runtime/boundRig';
import type { BoundRig } from '../runtime/boundRig';

export interface AssembledRig {
  /** Identity group holding every skinned mesh + the skeleton root. */
  root: Group;
  rig: BoundRig;
  /** The entry actually used for each mesh, in MESH order (see `resolveSkinData`). */
  skinData: SkinData[];
  /** The three.js skeleton the meshes are bound to (same object as `built.skeleton`). */
  skeleton: Skeleton;
  /**
   * The full built canonical skeleton — root bone, bones by canonical id, and the `Skeleton`.
   * Callers that only need the bound rig can ignore it; the editor stores it.
   */
  built: BuiltCanonicalSkeleton;
  skinnedMeshes: SkinnedMesh[];
}

/**
 * Pair every mesh with the `SkinData` that belongs to it: **by `meshName` first, positionally as
 * the fallback** — the rule the editor already used when restoring a saved project, now the one
 * rule for every caller. Stored weights survive a re-import that reorders the meshes; freshly
 * solved weights (already in mesh order, and possibly unnamed) keep pairing positionally.
 *
 * A name is consumed once, so two meshes sharing a name take the first and the second entry of
 * that name rather than both taking the first — which is what positional pairing would give and
 * therefore what the live re-skin path expects.
 *
 * Extra entries that no mesh claims are ignored, exactly as the restore path ignored them.
 */
function resolveSkinData(meshes: Mesh[], skinData: SkinData[]): SkinData[] {
  const byName = new Map<string, SkinData[]>();
  for (const entry of skinData) {
    if (!entry.meshName) continue;
    const bucket = byName.get(entry.meshName);
    if (bucket) bucket.push(entry);
    else byName.set(entry.meshName, [entry]);
  }

  return meshes.map((mesh, i) => {
    const bucket = mesh.name ? byName.get(mesh.name) : undefined;
    const matched = (bucket && bucket.length > 0 ? bucket.shift() : undefined) ?? skinData[i];
    if (!matched) {
      throw new AppError(
        'SKIN_DATA_MISSING',
        `No stored skin weights for mesh "${mesh.name || `mesh${i}`}". Regenerate them.`,
        { meshName: mesh.name, index: i },
      );
    }
    return matched;
  });
}

/**
 * Build a skinned, bound canonical rig from an imported (unrigged) model's meshes, a
 * joint placement and per-mesh skin weights. Pure construction: touches no editor state.
 *
 * `skinData` is paired to `meshes` by `resolveSkinData` (name first, positional fallback), so the
 * caller may hand over stored entries in any order. A mesh no entry can be paired with throws
 * `AppError('SKIN_DATA_MISSING')`; vertex-count and influence-count mismatches throw
 * `AppError('SKIN_DATA_MISMATCH')` from `createSkinnedMesh`.
 */
export function assembleSkinnedRig(
  meshes: Mesh[],
  placement: CanonicalRigPlacement,
  skinData: SkinData[],
): AssembledRig {
  const resolved = resolveSkinData(meshes, skinData);

  const built = buildCanonicalSkeleton(placement);

  const skinned = meshes.map((mesh, i) => createSkinnedMesh(mesh, built, resolved[i]));

  const root = new Group();
  root.name = 'MotionKitRiggedRoot';
  root.position.set(0, 0, 0);
  root.quaternion.identity();
  root.scale.set(1, 1, 1);
  for (const mesh of skinned) root.add(mesh);
  root.add(built.root);
  root.updateMatrixWorld(true);

  // The canonical skeleton IS the exported skeleton, so W_b and the world correction are the
  // identity map (D5) — but the placement itself is not the canonical T-pose (an A-pose model
  // gets an A-pose placement), so the binding still carries the rest alignment measured from
  // where the joints actually ended up. `identityBinding()` would drop exactly that.
  const rig = bindRig(root, bindingForPlacement(builtRestJointPositions(built)));

  return { root, rig, skinData: resolved, skeleton: built.skeleton, built, skinnedMeshes: skinned };
}
